// Change C — the visible confidence indicator (plan §4.3-C3 / doc 42 §3.3).
//
// A small floating badge ("Measured" / "Literature" / "Model" / "Synthetic")
// tinted by tier, keyed to the region currently under the map center. It renders
// NOTHING when the region under the center has no tier (parseConfidence ->
// undefined) — which is the case for all three existing regions (EU/NA/Asia ship
// no `confidence` string), so this is INVISIBLE and INERT for them. The "region
// in view" test uses each region's geo.bbox (data already in the manifest); no
// geometry/shader/render-path change.

import { CONFIDENCE_META, type ConfidenceTier } from "../confidence";

/** A confidence sub-area carried by a region's manifest (`confidenceZones`). */
export interface BadgeZone {
  /** Zone bounds in lon/lat (from manifest.confidenceZones[].bbox). */
  bbox: {
    lon_min: number;
    lon_max: number;
    lat_min: number;
    lat_max: number;
  };
  /** Zone tier (already validated to the 4-enum; otherwise omit the zone). */
  tier: ConfidenceTier;
  /** Optional human label for this sub-area. */
  label?: string;
}

/** Minimal per-region descriptor the badge needs (tier may be undefined). */
export interface BadgeRegion {
  key: string;
  label: string;
  /** Region bbox in lon/lat (from manifest.geo.bbox). */
  bbox?: {
    lon_min?: number;
    lon_max?: number;
    lat_min?: number;
    lat_max?: number;
  };
  /** Parsed region tier (undefined => this region contributes no badge). */
  tier?: ConfidenceTier;
  /**
   * Optional sub-area tier overrides (from manifest.confidenceZones). When the
   * map center falls inside one of these, the badge shows the ZONE tier instead
   * of the region default. Absent on the 3 existing regions, so they stay inert.
   */
  zones?: BadgeZone[];
}

export class ConfidenceBadge {
  readonly el: HTMLElement;
  private regions: BadgeRegion[];

  constructor(regions: BadgeRegion[]) {
    this.regions = regions;
    this.el = document.createElement("div");
    this.el.className = "confidence-badge";
    this.el.setAttribute("role", "status");
    this.el.setAttribute("aria-live", "polite");
    // Hidden until a tiered region is under the center. If NO region has a tier
    // at all, this element stays hidden forever (existing-region case).
    this.el.hidden = true;
  }

  /** True iff any region carries a tier (so the host can skip wiring it). */
  hasAnyTier(): boolean {
    return this.regions.some((r) => r.tier !== undefined);
  }

  /** Update the badge for the current map center [lng, lat]. */
  update(center: { lng: number; lat: number }): void {
    const region = this.regionAt(center.lng, center.lat);
    if (!region || region.tier === undefined) {
      this.el.hidden = true;
      this.el.textContent = "";
      return;
    }
    // Prefer a sub-area tier when the center falls inside one of the region's
    // `confidenceZones` (e.g. the SE-AU "measured" zone); otherwise fall back to
    // the region default tier. Regions without zones keep the region tier — and
    // tier-less regions returned above, so this is inert for the existing 3.
    const zone = this.zoneAt(region, center.lng, center.lat);
    const tier = zone?.tier ?? region.tier;
    const label = zone?.label ?? region.label;
    const meta = CONFIDENCE_META[tier];
    this.el.hidden = false;
    this.el.style.setProperty("--conf-color", meta.color);
    this.el.innerHTML =
      `<span class="conf-dot" aria-hidden="true"></span>` +
      `<span class="conf-text"><strong>${meta.label}</strong>` +
      ` data — ${escapeHtml(label)}</span>`;
  }

  /** The most specific zone of `region` containing the point, if any. */
  private zoneAt(
    region: BadgeRegion,
    lng: number,
    lat: number,
  ): BadgeZone | undefined {
    const zones = region.zones;
    if (!zones || zones.length === 0) return undefined;
    // First containing zone wins. Zones are validated to the 4-enum at the call
    // site, so any present zone tier is safe to display.
    return zones.find((z) => {
      const b = z.bbox;
      return (
        lng >= b.lon_min &&
        lng <= b.lon_max &&
        lat >= b.lat_min &&
        lat <= b.lat_max
      );
    });
  }

  /** The first region whose bbox contains the point, preferring a tiered one. */
  private regionAt(lng: number, lat: number): BadgeRegion | undefined {
    const inBbox = (r: BadgeRegion): boolean => {
      const b = r.bbox;
      if (!b) return false;
      const lonMin = b.lon_min ?? -Infinity;
      const lonMax = b.lon_max ?? Infinity;
      const latMin = b.lat_min ?? -Infinity;
      const latMax = b.lat_max ?? Infinity;
      return lng >= lonMin && lng <= lonMax && lat >= latMin && lat <= latMax;
    };
    // Prefer a tiered region under the center; fall back to any region (so an
    // untiered region under center correctly yields "no badge").
    return (
      this.regions.find((r) => r.tier !== undefined && inBbox(r)) ??
      this.regions.find((r) => inBbox(r))
    );
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
